import apiClient from './api-client';

// Updated interfaces based on the new backend field mappings

export interface LoanBenchmark {
  loan_id: number; // Changed from rp_system_id
  loan_name: string;
  current_balance: number;
  benchmark_type: string;
  benchmark_yield: number;
  market_yield: number; // Maps to component_yield_pct
  market_yield_decimal: number; // Maps to component_yield_decimal
  total_spread_bps: number; // Was effective_spread_bps, now rpx_total_spread_bps
  base_spread_bps: number; // Was total_rpx_adjustment_bps, now rpx_base_spread_bps
  coupon: number;
  fair_value_clean: number; // Was fair_value
  fair_value_dirty: number; // Was market_value
  price_clean_pct: number; // Was price_pct
  price_dirty_pct: number;
  wal_years: number;
  modified_duration_years: number;
  convexity: number;
  calculation_method: string; // Maps to npv_methodology
  valuation_date: string;
  settlement_date: string;
  last_updated: string; // Was materialized_at
}

export interface LoanWALEnhanced {
  loan_id: number;
  client_loan_number: string;
  loan_name: string;
  current_balance: number;
  original_balance: number;
  wal_years: number;
  remaining_term_years: number;
  average_life_months: number;
  maturity_date: string;
  calculation_date: string;
  macaulay_duration_years: number;
  modified_duration_years: number;
  convexity: number;
  original_maturity_date: string;
  origination_date: string;
  total_periods: number;
  final_period: number;
  first_amort_period: number;
  io_end_date: string;
  fair_value_clean: number;
  price_clean_pct: number;
  yield_to_maturity: number; // Maps to component_yield_decimal
}

export interface LoanAccrued {
  loan_id: number;
  loan_name: string;
  current_balance: number;
  accrued_interest: number;
  fair_value_clean: number; // Was fair_value
  fair_value_dirty: number; // Was market_value
  price_clean_pct: number; // Was price_pct
  price_dirty_pct: number;
  price_accrued_pct: number;
  gross_proceeds: number; // Was total_investment_value
  net_proceeds: number;
  npv_value: number;
  coupon: number;
  periodicity: string;
  valuation_date: string;
  settlement_date: string;
  last_updated: string; // Was materialized_at
}

export interface PortfolioSummaryEnhanced {
  total_loans: number;
  total_balance: number;
  total_fair_value: number; // Now uses SUM(fair_value_clean)
  total_market_value: number; // Now uses SUM(fair_value_dirty)
  avg_price: number; // Now uses AVG(price_clean_decimal)
  avg_price_pct: number; // Now uses AVG(price_clean_pct)
  avg_yield: number;
  avg_duration: number;
  avg_wal: number;
  avg_effective_spread_bps: number; // Now uses AVG(rpx_total_spread_bps)
  avg_rpx_adjustment_bps: number; // Now uses AVG(rpx_base_spread_bps)
  sector_breakdown: Record<
    string,
    {
      count: number;
      balance: number;
      fair_value: number;
    }
  >;
  status_breakdown: Record<
    string,
    {
      count: number;
      balance: number;
      fair_value: number;
    }
  >;
  last_update: string; // Now uses MAX(last_updated)
}

// New interfaces for new endpoints

export interface RiskMetrics {
  loan_id: number;
  loan_name: string;
  loan_status: string;
  property_type: string;
  sector: string; // Was property_sector
  current_balance: number;
  original_balance: number;
  ltv_current: number;
  dscr_current: number;
  internal_credit_rating: string;
  fair_value_clean: number;
  price_clean_pct: number;
  rpx_total_spread_bps: number; // Was effective_spread_bps
  rpx_base_spread_bps: number; // Was total_rpx_adjustment_bps
  market_yield: number;
  wal_years: number;
  modified_duration_years: number;
  effective_maturity_date: string;
  valuation_date: string;
  last_updated: string;
}

export interface PricingSummary {
  total_loans: number;
  total_current_balance: number;
  total_fair_value: number;
  avg_price_pct: number;
  avg_wal_years: number;
  avg_effective_spread_bps: number;
  performing_loans: number;
  non_performing_loans: number;
  last_update: string;
}

export interface PricingComponents {
  loan_id: number;
  loan_name: string;
  current_balance: number;
  coupon: string;
  benchmark_type: string;
  benchmark_yield: number;
  total_yield: number; // component_yield_decimal
  total_yield_pct: number; // component_yield_pct
  rpx_total_spread_bps: number;
  rpx_base_spread_bps: number;
  fair_value_clean: number;
  fair_value_dirty: number;
  accrued_interest: number;
  price_clean_decimal: number;
  price_dirty_decimal: number;
  price_clean_pct: number;
  price_dirty_pct: number;
  price_accrued_pct: number;
  npv_value: number;
  gross_proceeds: number;
  net_proceeds: number;
  npv_methodology: string;
  last_updated: string;
}

export interface CashflowAnalytics {
  loan_id: number;
  loan_name: string;
  current_balance: number;
  total_cashflows: number;
  total_periods: number;
  final_period: number;
  first_amort_period: number;
  periodicity: string;
  amortization_type: string;
  io_end_date: string;
  effective_maturity_date: string;
  wal_years: number;
  macaulay_duration_years: number;
  modified_duration_years: number;
  convexity: number;
  npv_value: number;
  irr: number; // component_yield_decimal
  last_updated: string;
}

export interface DetailedCashflow {
  period: number;
  date: string;
  cf_funded_balance: number;
  cf_draws: number;
  cf_unfunded_balance: number;
  unfunded_fee: number;
  original_balance: number;
  current_balance: number;
  interest: number;
  pik_interest: number;
  default_interest: number;
  principal: number;
  additional_fees: number;
  equity_kicker: number;
  cash_flow: number;
}

export interface DetailedCashflowResponse {
  loan_id: number;
  loan_summary: {
    current_balance: number;
    coupon_rate: number;
    maturity_date: string;
    periodicity: string;
    amortization_type: string;
    total_periods: number;
    total_interest: number;
    total_principal: number;
    total_payments: number;
  };
  cashflows: DetailedCashflow[];
}

// Fair Value Report types
export interface FairValueReportHeader {
  report_title: string;
  reporting_period: string;
  report_date: string;
  client_name: string;
  client_account: string;
  cre_loan_segment: string;
  loan_name: string;
  property_location_summary: string;
  fair_value_estimate: string;
  fair_value_percentage: string;
  valuation_date: string;
}

export interface FairValueReportLoanInformation {
  loan_number: string;
  property: string;
  property_location: string;
  addr_street: string;
  city_state: string;
  sector: string;
  property_type: string;
  loan_type_description: string;
  loan_amortization: string;
  loan_to_value_ratio: string;
  debt_service_coverage_ratio: string;
  sponsor_borrower: string;
}

export interface FairValueReportPricingAnalysis {
  valuation_date: string;
  settlement_date: string;
  price: string;
  benchmark_yield: string;
  credit_spread: string;
  market_yield_cbe: string;
  weighted_average_life: string;
  modified_duration: string;
  cash_flow_scenario: string;
  loan_status: string;
}

export interface FairValueReportContractDetails {
  origination_date: string;
  maturity_date: string;
  original_maturity_date: string;
  current_maturity_date: string;
  contract_type: string;
  contract_description: string;
  payment_frequency: string;
  day_count_convention: string;
}

export interface FairValueReportProceedsAnalysis {
  original_balance: string;
  current_balance: string;
  gross_proceeds: string;
  accrued_interest: string;
  net_proceeds: string;
}

export interface FairValueReportValuationDetails {
  valuation_date: string;
  settlement_date: string;
  original_balance: string;
  current_balance: string;
  gross_proceeds: string;
  accrued_interest: string;
  net_proceeds: string;
}

export interface FairValueReportValuationConclusion {
  loan_name: string;
  valuation_date: string;
  fair_value_estimate: string;
  fair_value_percentage: string;
  disclaimer: string;
}

export interface FairValueReportCashFlowItem {
  period: number;
  period_date: string;
  credit_facility_funded_balance: string;
  credit_facility_draws: string;
  credit_facility_unfunded_balance: string;
  unfunded_commitment_fee: string;
  original_balance_total_committed_line: string;
  current_balance_accrued_interest_and_pik_interest: string;
  interest: string;
  pik_interest: string;
  default_interest: string;
  principal: string;
  additional_fees: string;
  equity_kicker: string;
  cash_flow: string;
}

export interface FairValueReportSummaryOfCashFlows {
  title: string;
  cash_flows: FairValueReportCashFlowItem[];
}

export interface FairValueReportResponse {
  header: FairValueReportHeader;
  loan_information: FairValueReportLoanInformation;
  pricing_analysis: FairValueReportPricingAnalysis;
  contract_details: FairValueReportContractDetails;
  proceeds_analysis: FairValueReportProceedsAnalysis;
  valuation_details: FairValueReportValuationDetails;
  valuation_conclusion: FairValueReportValuationConclusion;
  summary_of_cash_flows: FairValueReportSummaryOfCashFlows;
}

// Complete pricing output interface (replaces the old pricing-output structure)
export interface CompletePricingOutput {
  loan_id: number;
  loan_name: string;
  current_balance: number;
  fair_value_clean: number; // Was fair_value
  fair_value_dirty: number; // Was market_value
  price_clean_decimal: number; // Was price
  price_clean_pct: number; // Was price_pct
  price_dirty_decimal: number;
  price_dirty_pct: number;
  accrued_interest: number;
  npv_value: number;
  gross_proceeds: number; // Was total_investment_value
  net_proceeds: number;
  coupon: number;
  benchmark_type: string;
  benchmark_yield: number;
  rpx_total_spread_bps: number; // Was effective_spread_bps
  rpx_base_spread_bps: number; // Was total_rpx_adjustment_bps
  component_yield_decimal: number; // Was market_yield_decimal
  component_yield_pct: number; // Was market_yield
  wal_years: number;
  modified_duration_years: number;
  convexity: number;
  effective_maturity_date: string;
  npv_methodology: string; // Was calculation_method
  valuation_date: string;
  settlement_date: string;
  last_updated: string; // Was materialized_at
}

export interface ValuationDownloadPayload {
	[key: string]: string | number | boolean;
}

export interface ValuationDownloadRequest {
	valuationDate: string;
	payload?: ValuationDownloadPayload;
}

// Service class with all valuation methods
export const valuationService = {
  // Enhanced existing endpoints
  async getLoanBenchmark(): Promise<LoanBenchmark[]> {
    const response = await apiClient.get('/api/v1/valuation/loan-benchmark');
    return response.data;
  },

  async getLoanWAL(): Promise<LoanWALEnhanced[]> {
    const response = await apiClient.get('/api/v1/valuation/loan-wal');
    return response.data;
  },

  async getLoanAccrued(): Promise<LoanAccrued[]> {
    const response = await apiClient.get('/api/v1/valuation/loan-accrued');
    return response.data;
  },

  async getPortfolioSummary(): Promise<PortfolioSummaryEnhanced> {
    const response = await apiClient.get('/api/v1/valuation/portfolio-summary');
    return response.data;
  },

  // New endpoints
  async getRiskMetrics(): Promise<RiskMetrics[]> {
    const response = await apiClient.get('/api/v1/valuation/risk-metrics');
    return response.data;
  },

  async getPricingComponents(loanId: number): Promise<PricingComponents[]> {
    const response = await apiClient.get(
      '/api/v1/valuation/pricing-components',
      {
        params: {
          loan_id: loanId,
        },
      }
    );
    return response.data;
  },
  async getPricingSummary(): Promise<PricingSummary[]> {
    const response = await apiClient.get('/api/v1/valuation/pricing-summary');
    return response.data;
  },

  async getCashflowAnalytics(): Promise<CashflowAnalytics[]> {
    const response = await apiClient.get('/api/v1/valuation/loan-cashflows');
    return response.data;
  },

  async getDetailedCashflows(
    loanId: number,
    maxPeriods: number = 625
  ): Promise<DetailedCashflowResponse> {
    const response = await apiClient.get(
      `/api/v1/valuation/loan-cashflows-detailed/${loanId}`,
      {
        params: { max_periods: maxPeriods },
      }
    );
    return response.data;
  },

  async getCompletePricingOutput(): Promise<CompletePricingOutput[]> {
    const response = await apiClient.get(
      '/api/v1/valuation/complete-pricing-output'
    );
    return response.data;
  },

  async getLoanFairValueReport(
    loanId: number
  ): Promise<FairValueReportResponse> {
    const response = await apiClient.get(
      '/api/v1/valuation/loan-fair-value-report',
      {
        params: { loan_id: loanId },
      }
    );
    return response.data;
  },

	/**
	 * Download AD&Co fixed rates payload as binary file.
	 */
	async downloadAddFixedRates({
		valuationDate,
		payload = {},
	}: ValuationDownloadRequest): Promise<Blob> {
		const response = await apiClient.post(
			'/api/v1/valuation/download-add-fixed-rates',
			{
				valuation_date: valuationDate,
				...payload,
			},
			{
				responseType: 'blob',
				headers: {
					Accept: 'application/octet-stream',
				},
			}
		);

		return response.data;
	},

	/**
	 * Download AD&Co forward curves payload as binary file.
	 */
	async downloadAddForwardCurves({
		valuationDate,
		payload = {},
	}: ValuationDownloadRequest): Promise<Blob> {
		const response = await apiClient.post(
			'/api/v1/valuation/download-add-forward-curves',
			{
				valuation_date: valuationDate,
				...payload,
			},
			{
				responseType: 'blob',
				headers: {
					Accept: 'application/octet-stream',
				},
			}
		);

		return response.data;
	},

  // Helper methods for field mapping migration
  mapLegacyToNewFields(
    legacyData: Record<string, unknown>
  ): Record<string, unknown> {
    return {
      ...legacyData,
      loan_id: legacyData.rp_system_id || legacyData.loan_id,
      fair_value_clean: legacyData.fair_value || legacyData.fair_value_clean,
      fair_value_dirty: legacyData.market_value || legacyData.fair_value_dirty,
      price_clean_decimal: legacyData.price || legacyData.price_clean_decimal,
      price_clean_pct: legacyData.price_pct || legacyData.price_clean_pct,
      rpx_total_spread_bps:
        legacyData.effective_spread_bps || legacyData.rpx_total_spread_bps,
      rpx_base_spread_bps:
        legacyData.total_rpx_adjustment_bps || legacyData.rpx_base_spread_bps,
      sector: legacyData.property_sector || legacyData.sector,
      last_updated: legacyData.materialized_at || legacyData.last_updated,
      npv_methodology:
        legacyData.calculation_method || legacyData.npv_methodology,
      component_yield_pct:
        legacyData.market_yield || legacyData.component_yield_pct,
      component_yield_decimal:
        legacyData.market_yield_decimal || legacyData.component_yield_decimal,
      gross_proceeds:
        legacyData.total_investment_value || legacyData.gross_proceeds,
    };
  },

  // Utility method to check if using new or legacy field names
  isNewFieldStructure(data: Record<string, unknown>): boolean {
    return 'fair_value_clean' in data && 'rpx_total_spread_bps' in data;
  },
};
